import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../styles/index.css';

// 👇 IMPORTAÇÃO DOS ALERTAS PADRONIZADOS
import { showSuccess, showError, showWarning } from '../utils/alertas';

import EdicaoModal from '../components/EdicaoModal';
import AdicionarMarcoModal from '../components/AdicionarMarcoModal';
import FluxogramaModal from '../components/FluxogramaModal';
import EditarContratoModal from '../components/EditarContratoModal';
import ExcluirModal from '../components/ExcluirModal';

// ⚠️ MANTENHA TRUE PARA PRODUÇÃO
const IS_PROD = true;

const API_BASE_URL = IS_PROD ? 'https://sigoapi.jhe-etes.com.br/api' : 'http://localhost:3001/api';
const SERVER_URL = IS_PROD ? 'https://sigoapi.jhe-etes.com.br' : 'http://localhost:3001';

function DetalheEmpreendimento() {
    const { id } = useParams();
    const navigate = useNavigate();

    // 🔐 1. RECUPERAÇÃO DO TOKEN
    const token = localStorage.getItem('token');
    const isAdmin = !!token;

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('Visão Geral');

    // Modais de Edição/Criação
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isMarcoModalOpen, setIsMarcoModalOpen] = useState(false);
    const [isFluxoModalOpen, setIsFluxoModalOpen] = useState(false);
    const [isContratoModalOpen, setIsContratoModalOpen] = useState(false);

    // 👇 ESTADOS PARA O MODAL DE EXCLUSÃO
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    const [selectedContratoId, setSelectedContratoId] = useState(null);
    const [marcoEmEdicao, setMarcoEmEdicao] = useState(null);
    const [contratoEmEdicao, setContratoEmEdicao] = useState(null);

    const [expandedContrato, setExpandedContrato] = useState(null);
    const [filtroContratoId, setFiltroContratoId] = useState('Todos');
    const [fluxoIndex, setFluxoIndex] = useState(0);

    const fetchDetalhes = useCallback(async () => {
        try {
            const headers = { 'Content-Type': 'application/json' };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${API_BASE_URL}/empreendimentos/${id}`, {
                method: 'GET',
                headers: headers
            });

            if (response.ok) setData(await response.json());
            else navigate('/404');
        } catch (error) { 
            console.error("Erro:", error);
            showError("Erro de Conexão", "Não foi possível carregar os dados.");
        }
        finally { setLoading(false); }
    }, [id, navigate, token]);

    useEffect(() => { fetchDetalhes(); }, [fetchDetalhes]);

    // ✅ Handlers Originais Preservados
    const openMarcoModal = (cId) => { setSelectedContratoId(cId); setMarcoEmEdicao(null); setIsMarcoModalOpen(true); };
    const handleCloseMarcoModal = () => { setIsMarcoModalOpen(false); setMarcoEmEdicao(null); };
    const handleEditarMarco = (m, cId) => { if (!isAdmin) return; setSelectedContratoId(cId); setMarcoEmEdicao(m); setIsMarcoModalOpen(true); };

    const handleEditarContrato = (contrato) => {
        setContratoEmEdicao(contrato);
        setIsContratoModalOpen(true);
    };

    const handleDeleteArquivo = (label) => {
        const tipo = label === 'Fluxograma' ? 'imagem' : 'video';
        setItemToDelete({ tipo, label });
        setIsDeleteModalOpen(true);
    };

    const confirmarExclusaoReal = async () => {
        if (!itemToDelete) return;

        try {
            const response = await fetch(`${API_BASE_URL}/empreendimentos/${id}/arquivo`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ tipo: itemToDelete.tipo })
            });

            if (response.ok) {
                await showSuccess("Removido", `${itemToDelete.label} removido com sucesso.`);
                fetchDetalhes(); 
                setFluxoIndex(0); 
                setIsDeleteModalOpen(false); 
                setItemToDelete(null);
            } else {
                const err = await response.json();
                showError("Erro", err.message || "Erro ao remover arquivo.");
            }
        } catch (error) {
            showError("Conexão", "Erro de conexão ao tentar excluir.");
        }
    };

    // --- HELPERS ORIGINAIS ---
    const formatMoney = (value) => {
        if (!value) return 'R$ 0,00';
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    };

    const formatData = (isoString) => {
        if (!isoString) return '-';
        const date = new Date(isoString);
        return date.toLocaleDateString('pt-BR') + ' às ' + date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    };

    const getTimestamp = (d) => {
        if (!d) return 9999999;
        const m = { 'Jan': 0, 'Fev': 1, 'Mar': 2, 'Abr': 3, 'Mai': 4, 'Jun': 5, 'Jul': 6, 'Ago': 7, 'Set': 8, 'Out': 9, 'Nov': 10, 'Dez': 11 };
        const p = d.split(' ');
        return p.length < 2 ? 9999999 : (parseInt(p[1]) * 100) + (m[p[0]] || 0);
    };

    const getFileUrl = (url) => {
        if (!url) return null;
        const t = new Date().getTime();
        if (url.startsWith('http')) return url;
        return `${SERVER_URL}${url.startsWith('/') ? url : '/' + url}?t=${t}`;
    };

    const getControleLabel = (valor) => {
        if (valor === 'Sim') return <span className="text-emerald-600 font-bold">Sim</span>;
        if (valor === 'Não' || valor === 'Nao') return <span className="text-red-500 font-bold">Não</span>;
        return <span className="text-gray-400 font-medium italic text-xs">Não Informado</span>;
    };

    const totais = useMemo(() => {
        if (!data || !data.contratos) return { valor: 0, qtd: 0 };
        const valorTotal = data.contratos.reduce((acc, c) => acc + (Number(c.valorContratoCadastroETE) || 0), 0);
        return { valor: valorTotal, qtd: data.contratos.length };
    }, [data]);

    const renderTopicos = (texto) => {
        if (!texto) return <span className="text-gray-400 italic text-sm">Informação não disponível.</span>;
        return (
            <ul className="space-y-2 mt-2">
                {texto.split(/[.\n]+/).filter(i => i.trim().length > 0).map((item, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-gray-600 leading-relaxed">
                        <span className="mt-1.5 w-1.5 h-1.5 bg-sabesp-blue rounded-full flex-shrink-0"></span><span>{item.trim()}.</span>
                    </li>
                ))}
            </ul>
        );
    };

    const renderFluxogramaContent = () => {
        const items = [];
        if (data?.fluxograma_url) items.push({ type: 'image', url: getFileUrl(data.fluxograma_url), label: 'Fluxograma' });
        if (data?.fluxograma_video_url) items.push({ type: 'video', url: getFileUrl(data.fluxograma_video_url), label: 'Vídeo' });

        if (items.length === 0) return (
            <div className="h-64 flex flex-col items-center justify-center text-gray-500 gap-4 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                <span className="text-4xl opacity-20">📐</span>
                <p className="text-sm font-medium">Nenhum arquivo.</p>
                {isAdmin && <button onClick={() => setIsFluxoModalOpen(true)} className="text-sabesp-cyan hover:underline text-xs font-bold">Adicionar</button>}
            </div>
        );

        const currentIndex = items[fluxoIndex] ? fluxoIndex : 0;
        const currentItem = items[currentIndex];

        return (
            <div className="relative w-full h-full group bg-gray-900 flex items-center justify-center overflow-hidden rounded-2xl border border-gray-200 shadow-inner">
                {currentItem.type === 'image' && (
                    <div className="relative w-full h-full bg-white flex items-center justify-center">
                        <img src={currentItem.url} alt="Fluxo" className="max-w-full max-h-full object-contain" />
                        <button onClick={() => window.open(currentItem.url, '_blank')} className="absolute bottom-4 right-4 bg-white text-gray-800 px-4 py-2 rounded-full text-xs font-bold shadow-lg hover:scale-105 transition-all flex items-center gap-2 z-20">🔍 Tela Cheia</button>
                    </div>
                )}
                {currentItem.type === 'video' && (
                    <div className="w-full h-full bg-black flex items-center justify-center">
                        <video src={currentItem.url} controls autoPlay loop playsInline className="w-full h-full max-h-[500px]">Vídeo não suportado.</video>
                    </div>
                )}

                {isAdmin && (
                    <button
                        onClick={() => handleDeleteArquivo(currentItem.label)}
                        className="absolute top-4 right-4 bg-white/90 hover:bg-red-50 text-red-500 hover:text-red-600 w-10 h-10 rounded-full flex items-center justify-center shadow-lg backdrop-blur-sm z-30 transition-all duration-300 transform hover:scale-110 border border-red-100"
                    >
                        🗑️
                    </button>
                )}

                {items.length > 1 && (
                    <>
                        <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/60 text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider backdrop-blur-md z-10 border border-white/10">
                            {currentItem.label}
                        </div>
                        <button onClick={() => setFluxoIndex(prev => (prev === 0 ? items.length - 1 : prev - 1))} className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white text-gray-700 rounded-full flex items-center justify-center shadow-lg z-20 transition-all hover:scale-110">❮</button>
                        <button onClick={() => setFluxoIndex(prev => (prev === items.length - 1 ? 0 : prev + 1))} className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white text-gray-700 rounded-full flex items-center justify-center shadow-lg z-20 transition-all hover:scale-110">❯</button>
                    </>
                )}
            </div>
        );
    };

    if (loading) return <div className="flex h-screen items-center justify-center text-gray-400 font-medium bg-[#F8F9FA]">Carregando...</div>;
    if (!data) return <div className="flex h-screen items-center justify-center text-gray-500 bg-[#F8F9FA]">Empreendimento não encontrado.</div>;

    return (
        <div className="page-container relative bg-[#F8F9FA] min-h-screen pb-20 font-sans">

            {/* HEADER ORIGINAL */}
            <div className="relative w-full h-[350px] md:h-[400px] bg-slate-800 overflow-hidden shadow-2xl">
                {data.imagem_capa_url && (<div className="absolute inset-0 bg-cover bg-center opacity-90 mix-blend-overlay transition-opacity duration-1000" style={{ backgroundImage: `url('${getFileUrl(data.imagem_capa_url)}')` }}></div>)}
                <div className="absolute inset-0 bg-gradient-to-t from-[#B9BFF0]/40 via-[#48C3F0]/30 to-transparent"></div>
                <div className="absolute inset-0 container mx-auto px-6 flex flex-col justify-end pb-12 z-10">
                    <button onClick={() => navigate('/')} className="absolute top-8 left-6 text-white/80 hover:text-white flex items-center gap-2 text-sm font-bold bg-black/20 hover:bg-black/40 px-4 py-2 rounded-full backdrop-blur-sm transition-all border border-white/10">← Voltar</button>
                    <div className="max-w-4xl animate-slideUp">
                        <h1 className="text-3xl md:text-5xl font-black text-white mb-4 tracking-tight drop-shadow-lg">{data.nome}</h1>
                        <p className="text-blue-50 text-sm md:text-lg font-light leading-relaxed max-w-3xl drop-shadow-md border-l-4 border-sabesp-cyan pl-4 mb-6 line-clamp-2">{data.descricao_curta}</p>
                        {isAdmin && (<button onClick={() => setIsModalOpen(true)} className="flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl text-xs font-bold transition-all border border-white/20 backdrop-blur-md shadow-lg">⚙️ Gerenciar Obra</button>)}
                    </div>
                </div>
            </div>

            <div className="container mx-auto px-6 -mt-10 relative z-20">
                {/* 👇 CARDS ORIGINAIS COM BORDAS COLORIDAS */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">

                    {/* Card Vazão */}
                    <div className="bg-white p-4 rounded-2xl shadow-sm border-l-4 border-l-[#00AEEF]">
                        <div className="flex gap-3 mb-3">
                            <span className="text-xl p-2 rounded-lg bg-blue-50">💧</span>
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Vazão (L/s)</span>
                        </div>
                        <div className="flex justify-between border-b border-dashed border-gray-50 pb-1">
                            <span className="text-xs text-gray-500">Atual</span>
                            <span className="text-sm font-bold text-sabesp-blue">{data.vazao_atual_ls || '-'}</span>
                        </div>
                        <div className="flex justify-between pt-1">
                            <span className="text-xs text-gray-500">Futura</span>
                            <span className="text-sm font-bold text-gray-400">{data.vazao_futura_ls || '-'}</span>
                        </div>
                    </div>

                    {/* Card Controle de Vazão */}
                    <div className="bg-white p-4 rounded-2xl shadow-sm border-l-4 border-l-[#F59E0B]">
                        <div className="flex gap-3 mb-3">
                            <span className="text-xl p-2 rounded-lg bg-yellow-50">🎛️</span>
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Controle de Vazão</span>
                        </div>
                        <div className="flex justify-between border-b border-dashed border-gray-50 pb-1">
                            <span className="text-xs text-gray-500">Entrada</span>
                            <span className="text-sm">{getControleLabel(data.controle_vazao_entrada)}</span>
                        </div>
                        <div className="flex justify-between pt-1">
                            <span className="text-xs text-gray-500">Saída</span>
                            <span className="text-sm">{getControleLabel(data.controle_vazao_saida)}</span>
                        </div>
                    </div>

                    {/* Card População */}
                   <div className="bg-white p-4 rounded-2xl shadow-sm border-l-4 border-l-[#8B5CF6]">
                        <div className="flex gap-3 mb-3">
                            <span className="text-xl p-2 rounded-lg bg-purple-50">👥</span>
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">População</span>
                        </div>
                        <div className="flex justify-between border-b border-dashed border-gray-50 pb-1">
                            <span className="text-xs text-gray-500">Atual</span>
                            <span className="text-sm font-bold text-gray-800">{data.populacao_atual?.toLocaleString() || '-'}</span>
                        </div>
                        <div className="flex justify-between pt-1">
                            <span className="text-xs text-gray-500">Futura</span>
                            <span className="text-sm font-bold text-purple-600">{data.populacao_futura?.toLocaleString() || '-'}</span>
                        </div>
                    </div>

                    {/* Card Investimento */}
                    <div className="bg-white p-4 rounded-2xl shadow-sm border-l-4 border-l-[#0C336A]">
                        <div className="flex gap-3 mb-3">
                            <span className="text-xl p-2 rounded-lg bg-slate-100">💰</span>
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Investimento</span>
                        </div>
                        <div className="mt-auto">
                            <p className="text-lg font-bold text-[#0C336A] truncate">{formatMoney(totais.valor)}</p>
                            <p className="text-xs text-gray-400">{totais.qtd} contratos</p>
                        </div>
                    </div>

                    {/* Card Corpo Hídrico */}
                    <div className="bg-white p-4 rounded-2xl shadow-sm border-l-4 border-l-[#10B981]">
                        <div className="flex gap-3 mb-3">
                            <span className="text-xl p-2 rounded-lg bg-emerald-50">🌊</span>
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Corpo Hídrico</span>
                        </div>
                        <div className="mt-auto">
                            <p className="text-sm font-bold text-gray-800 truncate">{data.corpo_hidrico || "N/A"}</p>
                            <span className="text-[10px] bg-teal-50 text-teal-700 px-2 py-0.5 rounded border border-teal-100 mt-1 inline-block">
                                {data.classe_rio || '-'}
                            </span>
                        </div>
                    </div>

                    {/* Card Local */}
                    <div className="bg-white p-4 rounded-2xl shadow-sm border-l-4 border-l-[#EF4444]">
                        <div className="flex gap-3 mb-3">
                            <span className="text-xl p-2 rounded-lg bg-red-50">📍</span>
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Local</span>
                        </div>
                        <div className="mt-auto">
                            <p className="text-sm font-bold text-gray-800 truncate">{data.nome_municipio || '-'}</p>
                            <p className="text-[10px] text-gray-400 truncate mt-0.5" title={data.endereco}>
                                {data.endereco || "Endereço não informado"}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-1.5 flex gap-1 w-full md:w-fit mb-6">
                    {['Visão Geral', 'Contratos e Execução'].map((tab) => (
                        <button key={tab} onClick={() => setActiveTab(tab)} className={`flex-1 md:flex-none px-6 py-2.5 rounded-xl text-sm font-bold transition-all ${activeTab === tab ? 'bg-sabesp-blue text-white shadow-md' : 'text-gray-500 hover:bg-gray-50'}`}>{tab}</button>
                    ))}
                </div>

                <div className="min-h-[400px]">
                    {activeTab === 'Visão Geral' && (
                        <div className="space-y-8 animate-fadeIn py-4">
                            {data.historia_narrativa && data.historia_narrativa.trim() !== "" && (
                                <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm">
                                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2"><span>📖</span> Concepção de Tratamento</h4>
                                    <p className="text-gray-600 leading-loose text-justify font-light text-sm">{data.historia_narrativa}</p>
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="bg-sky-50/30 border border-sky-100 p-8 rounded-3xl"><div className="flex items-center gap-2 mb-4"><span className="bg-sky-100 p-1.5 rounded-lg text-lg">⚡</span><h4 className="font-bold text-sky-900 text-sm uppercase tracking-wide">Diferenciais</h4></div><div className="text-sm text-sky-900/80 leading-relaxed">{renderTopicos(data.texto_diferenciais || data.texto_tecnologia)}</div></div>
                                <div className="bg-emerald-50/30 border border-emerald-100 p-8 rounded-3xl"><div className="flex items-center gap-2 mb-4"><span className="bg-emerald-100 p-1.5 rounded-lg text-lg">👥</span><h4 className="font-bold text-emerald-900 text-sm uppercase tracking-wide">Benefícios</h4></div><div className="text-sm text-emerald-900/80 leading-relaxed">{renderTopicos(data.texto_beneficios)}</div></div>
                            </div>

                            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm">
                                <div className="flex justify-between items-center mb-6"><h3 className="text-lg font-bold text-gray-800 flex items-center gap-2"><span>📐</span> Fluxograma & Vídeo</h3>{isAdmin && (<button onClick={() => setIsFluxoModalOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-gray-50 text-gray-600 rounded-lg text-xs font-bold hover:bg-gray-100 border border-gray-200 transition-all uppercase tracking-wider">📤 Atualizar Arquivos</button>)}</div>
                                <div className="aspect-video w-full max-h-[600px] mb-8">{renderFluxogramaContent()}</div>
                                {isAdmin && data.fluxograma_historico && data.fluxograma_historico.length > 0 && (
                                    <div className="pt-6 border-t border-gray-100 animate-fadeIn">
                                        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2"><span>🕒</span> Histórico de Versões (Restrito)</h4>
                                        <div className="space-y-3">{data.fluxograma_historico.map((item, index) => (<div key={index} className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl border border-slate-100"><div className="shrink-0 pt-1"><div className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center font-bold text-xs">U</div></div><div className="flex-1"><div className="flex justify-between items-start"><div className="flex flex-col"><p className="text-sm font-bold text-gray-800">{item.usuario || 'Usuário'}</p><p className="text-[10px] text-gray-400 font-mono">{formatData(item.data_registro)}</p></div>{item.arquivo_url && (<button onClick={() => window.open(getFileUrl(item.arquivo_url), '_blank')} className="text-[10px] bg-white border px-3 py-1 rounded-full font-bold">📄 Ver Arquivo</button>)}</div><p className="text-xs text-gray-600 mt-2">{item.descricao}</p></div></div>))}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

{/* --- ABA CONTRATOS --- */}
{activeTab === 'Contratos e Execução' && (
    <div className="py-4 space-y-6 animate-fadeIn">
        {data.contratos && data.contratos.length > 0 && (
            <div className="flex justify-end items-center gap-2 mb-2">
                <label className="text-xs font-bold text-gray-400 uppercase">Filtrar:</label>
                <select 
                    value={filtroContratoId} 
                    onChange={(e) => setFiltroContratoId(e.target.value)} 
                    className="bg-white border border-gray-200 text-gray-700 text-xs font-bold py-2 px-4 rounded-xl outline-none shadow-sm"
                >
                    <option value="Todos">Todos os Contratos</option>
                    {data.contratos.map(c => (
                        <option key={c.id} value={c.id}>{c.numero_contrato || c.codigo_contrato} - {c.nome}</option>
                    ))}
                </select>
            </div>
        )}

        {(filtroContratoId === 'Todos' ? data.contratos : data.contratos.filter(c => c.id === parseInt(filtroContratoId))).map((contrato) => {
            const isExpanded = expandedContrato === contrato.id;
            const marcosOrdenados = contrato.marcos ? [...contrato.marcos].sort((a, b) => getTimestamp(a.data_marco) - getTimestamp(b.data_marco)) : [];
            const descricaoContrato = contrato.objetoResumidoETE || contrato.descricao;

            return (
                <div key={contrato.id} className={`bg-white rounded-3xl border transition-all duration-300 overflow-hidden ${isExpanded ? 'border-sabesp-blue/30 shadow-md' : 'border-gray-200 shadow-sm hover:shadow-md'}`}>
                    
                    <div 
                        onClick={() => setExpandedContrato(isExpanded ? null : contrato.id)} 
                        className="cursor-pointer bg-slate-50/50 hover:bg-slate-50 px-4 md:px-6 py-5 flex flex-col md:flex-row md:items-center justify-between transition-colors gap-4"
                    >
                        <div className="flex items-center gap-4 flex-1">
                            <div className={`w-10 h-10 shrink-0 rounded-full flex items-center justify-center font-bold text-lg transition-transform duration-300 ${isExpanded ? 'bg-sabesp-blue text-white rotate-90' : 'bg-white border border-gray-200 text-gray-400'}`}>
                                ›
                            </div>
                            
                            <div className="flex-1">
                                <div className="flex flex-wrap items-center gap-2 mb-1">
                                    <span className="text-[10px] md:text-xs font-mono font-bold text-gray-500 bg-white border border-gray-200 px-2 py-0.5 rounded shadow-sm">
                                        {contrato.codigo_contrato || "S/N"}
                                    </span>
                                    <span className={`text-[9px] md:text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded text-white`} style={{ backgroundColor: contrato.status_cor || '#999' }}>
                                        {contrato.status_nome || 'Sem Status'}
                                    </span>
                                </div>
                                <h3 className="font-bold text-gray-800 text-sm md:text-lg leading-tight">{contrato.nome}</h3>
                            </div>
                        </div>

                        <div className="flex items-center justify-between md:justify-end gap-4 md:gap-8 border-t md:border-t-0 pt-4 md:pt-0">
                            <div className="text-left md:text-right">
                                <p className="text-[9px] md:text-[10px] font-bold text-gray-400 uppercase tracking-wider">Investimento</p>
                                <p className="font-bold text-sabesp-blue text-xs md:text-sm">{formatMoney(contrato.valorContratoCadastroETE)}</p>
                            </div>

                            {isAdmin && (
                                <div className="flex gap-2 shrink-0">
                                    <button 
                                        onClick={(e) => { e.stopPropagation(); handleEditarContrato(contrato); }} 
                                        className="w-9 h-9 md:w-8 md:h-8 flex items-center justify-center bg-white md:bg-gray-100 hover:bg-yellow-100 hover:text-yellow-700 text-gray-500 rounded-lg transition-colors border border-gray-200 shadow-sm md:shadow-none" 
                                        title="Editar Escopo e Valor"
                                    >
                                        ✏️
                                    </button>
                                    <button 
                                        onClick={(e) => { e.stopPropagation(); openMarcoModal(contrato.id); }} 
                                        className="px-3 py-2 md:py-1.5 bg-white border border-gray-200 hover:border-sabesp-blue text-sabesp-blue rounded-lg text-[10px] md:text-xs font-bold transition-all shadow-sm flex items-center gap-1"
                                    >
                                        <span>+</span> Marco
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {isExpanded && (
                        <div className="p-4 md:p-8 border-t border-gray-100 bg-white animate-fadeIn">
                            {descricaoContrato && descricaoContrato.trim() !== "" && (
                                <div className="mb-8 bg-gray-50 p-4 md:p-6 rounded-2xl border border-gray-100">
                                    <h4 className="text-[10px] md:text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Escopo</h4>
                                    <p className="text-xs md:text-sm text-gray-700 leading-relaxed text-justify">{descricaoContrato}</p>
                                </div>
                            )}
                            
                            <h4 className="text-[10px] md:text-xs font-bold text-gray-400 uppercase tracking-widest mb-6 flex items-center gap-2">
                                <span>🚀</span> LINHA DO TEMPO
                            </h4>

                            {marcosOrdenados.length > 0 ? (
                                <div className="relative overflow-x-auto pb-10 scrollbar-thin px-2">
                                    <div className="flex gap-0 min-w-max items-start pt-2">
                                        {marcosOrdenados.map((marco, idx) => {
                                            const isCompleted = marco.status === 'Concluído';
                                            const isInProgress = marco.status === 'Em andamento';
                                            const isLast = idx === marcosOrdenados.length - 1;

                                            let circleColor = "bg-white border-gray-200 text-gray-400";
                                            let textColor = "text-gray-800";
                                            let dateColor = "text-gray-400";
                                            let barColor = "bg-gray-200";

                                            if (isCompleted) {
                                                circleColor = "bg-emerald-500 border-emerald-200 text-white";
                                                barColor = "bg-emerald-400";
                                            } else if (isInProgress) {
                                                circleColor = "bg-blue-500 border-blue-200 text-white animate-pulse";
                                                textColor = "text-blue-700 font-bold";
                                                dateColor = "text-blue-500";
                                                barColor = "bg-blue-200";
                                            }

                                            return (
                                                <div key={idx} className="relative flex flex-col items-center w-32 md:w-40 group">
                                                    {!isLast && (<div className={`absolute top-[18px] left-[50%] w-full h-[3px] -z-0 ${barColor}`}></div>)}
                                                    {/* 👇 AS BOLINHAS ORIGINAIS ESTÃO AQUI */}
                                                    <div className={`w-8 h-8 md:w-10 md:h-10 rounded-full border-4 shadow-sm z-10 flex items-center justify-center transition-all duration-300 ${circleColor}`}>
                                                        {isCompleted ? <span className="text-sm md:text-lg font-bold">✓</span> : <span className="font-bold text-xs">{idx + 1}</span>}
                                                    </div>
                                                    <div className="mt-3 text-center px-1 w-full">
                                                        <span className={`block text-[9px] md:text-[10px] font-bold uppercase mb-1 ${dateColor}`}>{marco.data_marco}</span>
                                                        <p className={`text-[10px] md:text-xs leading-tight mb-2 h-12 flex items-center justify-center ${textColor}`}>{marco.titulo}</p>
                                                        {isAdmin && (
                                                            <button onClick={() => handleEditarMarco(marco, contrato.id)} className="mt-1 text-[9px] md:text-[10px] text-sabesp-blue font-bold hover:underline opacity-60 hover:opacity-100">Editar</button>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                                    <p className="text-gray-400 text-xs">Nenhum marco cadastrado.</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            );
        })}
    </div>
)}
                </div>
            </div>

            <footer className="footer-container text-center py-8 text-gray-400 text-xs mt-auto"><p>© 2026 Sabesp - Superintendência de Gestão de Empreendimentos</p></footer>

            <EdicaoModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} empreendimento={data} onUpdateSuccess={() => { fetchDetalhes(); setIsModalOpen(false); }} />
            <AdicionarMarcoModal isOpen={isMarcoModalOpen} onClose={handleCloseMarcoModal} empreendimentoId={data.id} contratoId={selectedContratoId} marcoParaEditar={marcoEmEdicao} onSuccess={() => { fetchDetalhes(); handleCloseMarcoModal(); }} />
            <FluxogramaModal isOpen={isFluxoModalOpen} onClose={() => setIsFluxoModalOpen(false)} empreendimentoId={data.id} currentData={data} onSuccess={() => { fetchDetalhes(); setIsFluxoModalOpen(false); }} />
            <EditarContratoModal isOpen={isContratoModalOpen} onClose={() => setIsContratoModalOpen(false)} contrato={contratoEmEdicao} onSuccess={fetchDetalhes} />

            <ExcluirModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={confirmarExclusaoReal}
                titulo="Remover Arquivo"
                mensagem={`Tem certeza que deseja remover este ${itemToDelete?.label || 'arquivo'}?\nEssa ação não poderá ser desfeita e ficará registrada no histórico.`}
                item={itemToDelete?.label}
            />
        </div>
    );
}

export default DetalheEmpreendimento;